import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Modal from '@app/react-common/components/modal/Modal';
import Button from '@app/react-common/components/button/Button';
import Input from '@app/react-common/components/input/Input';
import { Tab, Tabs } from '@app/react-common/components/tabs/Tabs';
import Dropdown, { Option } from '@app/react-common/components/dropdown/Dropdown';
import { AbTestOptimizationTarget } from '@pdqdev/pdq-shared/dist/entities';
import {
  TrafficAllocationInputV2,
  TrafficAllocationVariant,
} from '@app/react-common/components/traffic-allocation-input_V2/TrafficAllocationInputV2';
import { AbtestRuleEngineConfig } from './config/AbTestRuleEngineConfig';
import { AbTest } from '../../models/AbTest';
import { AbTestVariant } from '../../models/AbTestVariant';
import { useAppContext } from '@app/react-common/AppContext';
import useCheckouts from './hooks/useCheckouts';
import { numberToLetter } from '@utils/number-utils';
import { getKeyByStringValue } from '@utils/string-utils';
import { getCheckoutStatus, moveLiveCheckoutToTopOfTheList } from '@app/react-common/utils/CheckoutUtils';
import TextArea from '@app/react-common/components/text-area/TextArea';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { AbTestGroupMode } from '@pdqdev/pdq-shared/dist/entities/AbTestGroupMode';
import { RuleEngineV2 } from '@app/react-common/components/rule-engine-v2/RuleEngineV2';
import { RuleEngineApiDto } from '../../models/AbTestRulesApiDto';
import {
  mapRuleEngineApiDtoToRuleEngineState,
  mapRuleEngineStateToRuleEngineApiDto,
  mapCheckoutSegmentRuleGroupsToRuleEngineState,
} from '@app/react-common/components/rule-engine-v2/utils/ruleEngineMapper';
import { RuleEngineState } from '@app/react-common/components/rule-engine-v2/models/RuleEngineState';
import { RuleGroup } from '@app/react-common/components/rule-engine-v2/models/RuleGroup';
import { usePdqCoreApi } from '@app/react-common/api/usePdqCoreApi';
import { countryCodeToDisplayNameMap } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { Checkbox } from '@app/react-common/components/checkbox/Checkbox';
import { AbTestTypeCategory, AbTestWidgetType } from '../../models/AbTestCreationType';
import { Property } from '@app/react-common/components/rule-engine-v2/models/RuleEngineConfig';
import { WarningBanner } from '@app/react-common/components/banners/WarningBanner';
import { AbTestModalHeader } from './shared/AbTestModalHeader';

const MAX_TEST_GROUPS = 4;

interface AbTestModalV2Props {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  saveTest?: (formData: AbTest) => Promise<number | undefined>;
  existingAbTest?: AbTest;
  mode: 'create' | 'edit' | 'view' | 'duplicate';
  runningAbTestCheckoutIds: number[];
  onTestTypeChange?: (testType: AbTestTypeCategory) => void;
}

export interface AbTestFormDataV2 {
  name: string;
  description?: string;
  numberOfCheckouts: number;
  optimizingFor: string;
  statisticalSignificance: number;
  testGroups: TrafficAllocationVariant[];
  controlGroups: TrafficAllocationVariant[];
  abTestRules?: RuleEngineApiDto;
  testType: AbTestTypeCategory;
}

export const AbTestModalV2: React.FC<AbTestModalV2Props> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  saveTest,
  existingAbTest,
  mode,
  runningAbTestCheckoutIds,
  onTestTypeChange,
}) => {
  const { shop$, showToast } = useAppContext();
  const [activeTab, setActiveTab] = React.useState('Details');
  const [shop, setShop] = React.useState<Shop>(null);
  const [testGroupsValid, setTestGroupsValid] = React.useState(false);
  const [controlGroupsValid, setControlGroupsValid] = React.useState(false);
  const [rulesValid, setRulesValid] = React.useState(mode == 'create' ? false : true);
  const [statisticalSignificanceError, setStatisticalSignificanceError] = useState<string | undefined>(undefined);
  const [fullFormValid, setFullFormValid] = React.useState(false);
  const [showInvalidStep, setShowInvalidStep] = React.useState(0);
  const { checkouts } = useCheckouts();
  const [formData, setFormData] = useState<AbTestFormDataV2>(getInitialFormData(existingAbTest));

  // Reset validation state when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setShowInvalidStep(0);
      setActiveTab('Details');
    }
  }, [isOpen]);

  const getRemainingControlPercentage = React.useMemo(() => {
    const testGroupsSum =
      formData?.testGroups?.filter((v) => v.isSelected).reduce((sum, v) => sum + v.percentageTraffic, 0) || 0;
    return 100 - testGroupsSum;
  }, [formData?.testGroups]);

  // Track selected test group IDs for dependency tracking
  const selectedTestGroupIds = React.useMemo(() => {
    const selected = formData?.testGroups?.filter((v) => v.isSelected) || [];
    return selected
      .map((tg) => tg.widgetExternalId)
      .sort()
      .join(',');
  }, [formData?.testGroups]);

  // Filter control groups to only include visible ones (not selected as test groups and live)
  const filteredControlGroups = React.useMemo(() => {
    return (
      formData?.controlGroups?.filter((controlGroup) => {
        const isSelectedAsTestGroup = formData?.testGroups?.some(
          (testGroup) => testGroup.isSelected && testGroup.widgetExternalId === controlGroup.widgetExternalId,
        );
        const checkout = checkouts?.find((c) => c.id === controlGroup.widgetExternalId);
        const isLive = checkout?.isLive ?? false;
        return !isSelectedAsTestGroup && isLive;
      }) || []
    );
  }, [formData?.controlGroups, formData?.testGroups, checkouts]);

  // Clear abTestRules when test groups change (for new AB tests only)
  // This allows merged rules to recalculate when user selects different test groups
  // The key prop on RuleEngineV2 ensures the component remounts with the new initial state
  React.useEffect(() => {
    if (!existingAbTest && formData?.abTestRules) {
      setFormData((prevData) => ({
        ...prevData,
        abTestRules: undefined,
      }));
    }
  }, [selectedTestGroupIds, existingAbTest]);

  // Extract and merge rule groups from selected test group checkouts (only for new AB tests)
  const initialRulesFromTestGroupsForNewABTest = React.useMemo(() => {
    // Skip if: editing existing test, test groups not valid, or no checkouts
    if (existingAbTest || !testGroupsValid || !checkouts) {
      return null;
    }

    const selectedTestGroups = formData?.testGroups?.filter((v) => v.isSelected) || [];
    if (selectedTestGroups.length === 0) {
      return null;
    }

    // Collect all rule groups from all segments of selected test group checkouts
    const allRuleGroups: RuleGroup[] = [];
    selectedTestGroups.forEach((testGroup) => {
      const checkout = checkouts.find((c) => c.id === testGroup.widgetExternalId);
      checkout?.checkoutShippingMethodGroupSegments?.forEach((segment) => {
        if (segment.rules?.ruleGroups) {
          allRuleGroups.push(...segment.rules.ruleGroups);
        }
      });
    });

    if (allRuleGroups.length === 0) {
      return null;
    }

    return mapCheckoutSegmentRuleGroupsToRuleEngineState(allRuleGroups);
  }, [existingAbTest, testGroupsValid, checkouts, selectedTestGroupIds]);

  // Shared helper to validate test groups and control groups
  const validateTestAndControlGroups = React.useCallback(
    (validateControlGroups: boolean = false) => {
      // Validate test groups
      const selectedTestGroups = formData?.testGroups?.filter((v) => v.isSelected) || [];
      const hasSelectedTestVariants = selectedTestGroups.length > 0;
      const testGroupsSum = selectedTestGroups.reduce((sum, v) => sum + v.percentageTraffic, 0);
      const hasValidTestPercentage = testGroupsSum >= 5 && testGroupsSum <= 95;
      const hasValidTestCount = selectedTestGroups.length <= MAX_TEST_GROUPS;
      setTestGroupsValid(hasSelectedTestVariants && hasValidTestPercentage && hasValidTestCount);

      // Validate control groups if requested - use the memoized filteredControlGroups
      if (validateControlGroups) {
        const selectedControlGroups = filteredControlGroups.filter((cg) => cg.isSelected);
        const hasSelectedControlVariants = selectedControlGroups.length > 0;
        setControlGroupsValid(hasSelectedControlVariants);
      }
    },
    [formData?.testGroups, filteredControlGroups],
  );

  const failureHandler = useCallback(async () => {
    showToast({ message: `An error has occurred. Please contact support if the problem persists.`, mode: 'danger' });
  }, []);
  const pdqCoreApi = usePdqCoreApi(failureHandler);

  useEffect(() => {
    if (!checkouts || checkouts.length === 0) return;

    moveLiveCheckoutToTopOfTheList(checkouts, []);

    const testGroupVariants =
      checkouts
        ?.filter((checkout) => !checkout.isLive)
        ?.map((checkout) => {
          const existingAbTestVariant: AbTestVariant = existingAbTest?.abTestVariants.find(
            (variant) =>
              variant.abTestWidgets.find((widget) => widget.type == AbTestWidgetType.CHECKOUT).externalId ===
                checkout.id && variant.name !== 'A',
          );

          const trafficAllocationVariant: TrafficAllocationVariant = {
            apiId: null,
            widgetExternalId: checkout.id,
            name: checkout.name,
            description: checkout.description,
            percentageTraffic: 0,
            checkoutStatus: getCheckoutStatus(checkout, runningAbTestCheckoutIds),
            isSelected: false,
            widgetApiId: undefined,
          };

          if (existingAbTestVariant) {
            trafficAllocationVariant.isSelected = true;
            trafficAllocationVariant.percentageTraffic = existingAbTestVariant.percentageTraffic;
            trafficAllocationVariant.apiId = existingAbTestVariant.id;
            trafficAllocationVariant.widgetApiId = existingAbTestVariant.abTestWidgets.find(
              (x) => x.type == AbTestWidgetType.CHECKOUT,
            ).id;
          }

          return trafficAllocationVariant;
        }) || [];

    const existingVariantA = existingAbTest?.abTestVariants.find((v) => v.name === 'A');
    const controlGroupWidgets =
      existingVariantA?.abTestWidgets?.filter((w) => w.type === AbTestWidgetType.CHECKOUT) || [];

    const totalControlPercentage = existingVariantA?.percentageTraffic || 0;
    const percentagePerControlGroup =
      controlGroupWidgets.length > 0 ? Math.floor(totalControlPercentage / controlGroupWidgets.length) : 0;
    const remainder =
      controlGroupWidgets.length > 0
        ? totalControlPercentage - percentagePerControlGroup * controlGroupWidgets.length
        : 0;

    const controlGroupVariants =
      checkouts?.map((checkout) => {
        const matchingWidget = controlGroupWidgets.find((widget) => widget.externalId === checkout.id);

        const trafficAllocationVariant: TrafficAllocationVariant = {
          apiId: null,
          widgetExternalId: checkout.id,
          name: checkout.name,
          description: checkout.description,
          percentageTraffic: 0,
          checkoutStatus: getCheckoutStatus(checkout, runningAbTestCheckoutIds),
          isSelected: false,
          widgetApiId: undefined,
        };

        if (matchingWidget && existingVariantA) {
          trafficAllocationVariant.isSelected = true;
          const widgetIndex = controlGroupWidgets.findIndex((w) => w.externalId === checkout.id);
          trafficAllocationVariant.percentageTraffic =
            widgetIndex === 0 ? percentagePerControlGroup + remainder : percentagePerControlGroup;
          trafficAllocationVariant.apiId = existingVariantA.id;
          trafficAllocationVariant.widgetApiId = matchingWidget.id;
        }

        return trafficAllocationVariant;
      }) || [];

    setFormData((prevData) => {
      return {
        ...prevData,
        testGroups: testGroupVariants,
        controlGroups: controlGroupVariants,
      };
    });
  }, [checkouts, existingAbTest, isOpen, runningAbTestCheckoutIds]);

  useEffect(() => {
    const subscription = shop$.subscribe((shop) => {
      if (shop) {
        setShop(shop);
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setFullFormValid(testGroupsValid && controlGroupsValid && rulesValid && formData?.name?.length > 0);
  }, [formData.name, testGroupsValid, controlGroupsValid, rulesValid]);

  // Validate test groups and control groups when formData is populated with existing test data
  useEffect(() => {
    if (
      (mode === 'edit' || mode === 'duplicate') &&
      existingAbTest &&
      existingAbTest?.testType === AbTestTypeCategory.SHIPPING_RATES &&
      formData?.testGroups?.length > 0 &&
      formData?.controlGroups?.length > 0
    ) {
      validateTestAndControlGroups(true);
    }
  }, [mode, existingAbTest, formData?.testGroups, formData?.controlGroups, checkouts, validateTestAndControlGroups]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name == 'statisticalSignificance') {
      if (isNaN(Number(value)) || Number(value) <= 0 || Number(value) > 100) {
        setStatisticalSignificanceError('Statistical significance must be between 1 and 100');
        return;
      } else {
        setStatisticalSignificanceError(undefined);
      }
    }

    if (name == 'numberOfCheckouts') {
      if (isNaN(Number(value)) || Number(value) < 0) {
        return;
      }
    }
    setFormDataValue(name, value);
  };

  const setFormDataValue = (name: string, value: any) => {
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const selectedTestGroups = formData.testGroups.filter((x) => x.isSelected);
      const selectedControlGroups = formData.controlGroups.filter((controlGroup) => {
        if (!controlGroup.isSelected) return false;

        const isSelectedAsTestGroup = formData.testGroups.some(
          (testGroup) => testGroup.isSelected && testGroup.widgetExternalId === controlGroup.widgetExternalId,
        );
        const checkout = checkouts?.find((c) => c.id === controlGroup.widgetExternalId);
        const isLive = checkout?.isLive ?? false;

        return !isSelectedAsTestGroup && isLive;
      });

      const controlVariantsTotalPercentage = getRemainingControlPercentage;

      const controlWidgets = selectedControlGroups.map((variant) => ({
        id: variant.widgetApiId ? variant.widgetApiId : undefined,
        externalId: variant.widgetExternalId,
        type: AbTestWidgetType.CHECKOUT as const,
      }));

      const existingVariantA = existingAbTest?.abTestVariants?.find((v) => v.name === 'A');
      const controlVariant: AbTestVariant = {
        id: existingVariantA?.id ? existingVariantA.id : undefined,
        name: 'A',
        percentageTraffic: controlVariantsTotalPercentage,
        description: null,
        abTestWidgets: controlWidgets,
      };

      const testVariants: AbTestVariant[] = selectedTestGroups.map((variant, idx) => ({
        id: variant.apiId ? variant.apiId : undefined,
        name: numberToLetter(idx + 2),
        percentageTraffic: variant.percentageTraffic,
        description: null,
        abTestWidgets: [
          {
            id: variant.widgetApiId ? variant.widgetApiId : undefined,
            externalId: variant.widgetExternalId,
            type: AbTestWidgetType.CHECKOUT,
          },
        ],
      }));

      await saveTest({
        ...existingAbTest,
        name: formData.name,
        description: formData.description,
        numberOfCheckouts: formData.numberOfCheckouts,
        optimizingFor: formData.optimizingFor,
        statisticalSignificance: formData.statisticalSignificance || 95,
        endedAt: existingAbTest ? existingAbTest.endedAt : undefined,
        startedAt: existingAbTest ? existingAbTest.startedAt : undefined,
        shopId: shop.id,
        abTestRules: formData.abTestRules,
        abTestVariants: [controlVariant, ...testVariants],
        mode: AbTestGroupMode.MULTIPLE_CONTROL_GROUPS,
      });
      onSaveSuccess();
    } catch (error) {
      onClose();
    }
  };

  const tabOrder = ['Details', 'Select test groups', 'Select control groups', 'Rules'];

  const getTabIndex = (tabName: string): number => {
    return tabOrder.indexOf(tabName);
  };

  const handleTabChange = (tab: string) => {
    if (mode === 'view') {
      setActiveTab(tab);
      return;
    }

    const currentTabIndex = getTabIndex(activeTab);
    const targetTabIndex = getTabIndex(tab);

    if (targetTabIndex < currentTabIndex) {
      setActiveTab(tab);
      return;
    }

    if (activeTab == 'Details') {
      handleDetailsValidation(tab);
    } else if (activeTab === 'Rules') {
      handleRulesValidation(tab);
    } else if (activeTab === 'Select test groups') {
      handleTestGroupsValidation(tab);
    } else if (activeTab === 'Select control groups') {
      handleControlGroupsValidation(tab);
    } else {
      setActiveTab(tab);
    }
  };

  const preventControlGroupsNavigationIfNoTestGroups = (nextTab: string): boolean => {
    if (nextTab === 'Select control groups' || nextTab === 'Rules') {
      const selectedTestGroups = formData?.testGroups?.filter((v) => v.isSelected) || [];
      if (selectedTestGroups.length === 0) {
        setActiveTab('Select test groups');
        setShowInvalidStep(3);
        return true;
      }
    }

    if (nextTab === 'Rules') {
      const selectedControlGroups = filteredControlGroups.filter((cg) => cg.isSelected);
      const hasSelectedVariants = selectedControlGroups.length > 0;
      if (!hasSelectedVariants) {
        setActiveTab('Select control groups');
        setShowInvalidStep(4);
        return true;
      }
    }
    return false;
  };

  const handleTestGroupsValidation = (nextTab: string) => {
    const selectedTestGroups = formData?.testGroups?.filter((v) => v.isSelected) || [];
    const hasSelectedVariants = selectedTestGroups.length > 0;
    const testGroupsSum = selectedTestGroups.reduce((sum, v) => sum + v.percentageTraffic, 0);
    const hasValidPercentage = testGroupsSum >= 5 && testGroupsSum <= 95;
    const hasValidCount = selectedTestGroups.length <= MAX_TEST_GROUPS;

    if (!hasSelectedVariants || !hasValidPercentage || !hasValidCount) {
      setShowInvalidStep(3);
    } else {
      setShowInvalidStep(0);
      if (!preventControlGroupsNavigationIfNoTestGroups(nextTab)) {
        setActiveTab(nextTab);
      }
    }
  };

  const handleControlGroupsValidation = (nextTab: string) => {
    // Check if any of the filtered (visible) control groups are selected
    const selectedControlGroups = filteredControlGroups.filter((cg) => cg.isSelected);
    const hasSelectedVariants = selectedControlGroups.length > 0;

    if (!hasSelectedVariants) {
      setShowInvalidStep(4);
      // Don't navigate if validation fails - keep user on current tab to see error
      return;
    } else {
      setShowInvalidStep(0);
      if (nextTab && !preventControlGroupsNavigationIfNoTestGroups(nextTab)) {
        setActiveTab(nextTab);
      }
    }
  };

  const handleDetailsValidation = (nextTab: string) => {
    if (
      formData?.name?.length > 0 &&
      formData?.description?.replace(/\s/g, '').length >= 100 &&
      formData?.statisticalSignificance > 0
    ) {
      setShowInvalidStep(0);
      if (!preventControlGroupsNavigationIfNoTestGroups(nextTab)) {
        setActiveTab(nextTab);
      }
    } else {
      setShowInvalidStep(1);
    }
  };

  const handleRulesValidation = (nextTab: string) => {
    if (!rulesValid) {
      setShowInvalidStep(2);
    } else {
      setShowInvalidStep(0);
      if (!preventControlGroupsNavigationIfNoTestGroups(nextTab)) {
        setActiveTab(nextTab);
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="ab-test-modal">
        <AbTestModalHeader
          mode={mode}
          testTypeCategory={AbTestTypeCategory.SHIPPING_RATES}
          onClose={onClose}
          onTestTypeChange={onTestTypeChange}
        />
        <Tabs
          activeTab={activeTab}
          setActiveTab={handleTabChange}
          clickableTabs={true}
          horizontalLineUnderTabs={true}
          scrollable={true}>
          <Tab label={'Details'} showTab={true}>
            <Input
              disabled={mode == 'view'}
              type="text"
              placeholder='Give your test a short, clear name (e.g. "Free shipping vs Paid")'
              wrapperDivClassName="ab-test-modal__input"
              label="Test name"
              name="name"
              required={true}
              value={formData.name}
              onChange={handleInputChange}
              showValidation={showInvalidStep == 1}
            />
            <TextArea
              disabled={mode == 'view'}
              placeholder='What do you expect to learn? (e.g. "Free shipping will increase checkout rate.")'
              label="Hypothesis"
              name="description"
              required={true}
              wrapperDivClassName="ab-test-modal__input"
              value={formData.description}
              onChange={handleInputChange}
              showValidation={showInvalidStep == 1}
              ignoreWhitespace={true}
              minLength={100}
              maxLength={1000}
              invalidMessage="❌ Hypothesis must be at least 100 non-space characters. Please provide a clear explanation of what you are testing and why."
            />
            <Input
              disabled={mode == 'view'}
              type="text"
              placeholder="Leave blank for unlimited"
              wrapperDivClassName="ab-test-modal__input"
              label="End test after X checkouts"
              name="numberOfCheckouts"
              value={formData.numberOfCheckouts || ''}
              onChange={handleInputChange}
              min={0}
            />
            <Dropdown
              disabled={mode == 'view'}
              label="Goal metric"
              options={Object.entries(AbTestOptimizationTarget).map((entry) => ({ value: entry[0], label: entry[1] }))}
              onSelect={(option: Option) => {
                setFormDataValue('optimizingFor', option.value);
              }}
              preSelectedOption={{
                label: AbTestOptimizationTarget[formData.optimizingFor],
                value: formData.optimizingFor,
              }}
            />

            <div className="ab-test-modal__actions">
              <Button
                onClick={onClose}
                text={'Cancel'}
                size={'medium'}
                variant={'secondary'}
                className="ab-test-modal__actions__cancel"
              />
              <Button
                onClick={() => {
                  if (mode === 'view') {
                    handleTabChange('Select test groups');
                  } else {
                    handleDetailsValidation('Select test groups');
                  }
                }}
                text={'Next'}
                size={'medium'}
                variant={'primary'}
              />
            </div>
          </Tab>
          <Tab label={'Select test groups'} showTab={true}>
            <div className={`ab-test-modal__validation ${showInvalidStep == 3 ? '' : 'hidden'}`}>
              {!formData?.testGroups?.some((v) => v.isSelected)
                ? 'Please select at least one checkout group to test'
                : formData?.testGroups?.filter((v) => v.isSelected).length > MAX_TEST_GROUPS
                  ? `Please select no more than ${MAX_TEST_GROUPS} test groups`
                  : 'Reserve at least 5% of traffic for the control group to proceed.'}
            </div>
            <p className="ab-test-modal__traffic__title">
              Select the checkout groups you want to test and set the percentage of traffic to allocate.
            </p>
            {formData?.testGroups?.length > 0 ? (
              <TrafficAllocationInputV2
                trafficAllocationConfig={{
                  variants: formData?.testGroups,
                }}
                onChange={(valid: boolean, variants: TrafficAllocationVariant[]) => {
                  setFormData((prevData) => ({ ...prevData, testGroups: variants }));

                  const selectedTestGroups = variants.filter((v) => v.isSelected);
                  const hasSelectedVariants = selectedTestGroups.length > 0;
                  const hasValidCount = selectedTestGroups.length <= MAX_TEST_GROUPS;
                  const testGroupsSum = selectedTestGroups.reduce((sum, v) => sum + v.percentageTraffic, 0);
                  const hasValidPercentage = testGroupsSum >= 5 && testGroupsSum <= 95;
                  const isValid = hasSelectedVariants && hasValidCount && hasValidPercentage;
                  setTestGroupsValid(isValid);

                  if (isValid && showInvalidStep === 3) {
                    setShowInvalidStep(0);
                  }
                }}
                viewMode={mode == 'view'}
                maxTestGroups={MAX_TEST_GROUPS}
              />
            ) : null}
            <div className="ab-test-modal__actions">
              <Button
                onClick={() => setActiveTab('Details')}
                text={'Back'}
                size={'medium'}
                variant={'secondary'}
                className="ab-test-modal__actions__cancel"
              />
              <Button
                onClick={() => {
                  if (mode === 'view') {
                    handleTabChange('Select control groups');
                  } else {
                    handleTestGroupsValidation('Select control groups');
                  }
                }}
                text={'Next'}
                size={'medium'}
                variant={'primary'}
              />
            </div>
          </Tab>
          <Tab label={'Select control groups'} showTab={true}>
            <div className={`ab-test-modal__validation ${!controlGroupsValid && showInvalidStep == 4 ? '' : 'hidden'}`}>
              {!formData?.controlGroups?.some((v) => v.isSelected)
                ? 'Please select at least one control checkout group'
                : 'Traffic allocation percentages must add up to 100%'}
            </div>
            <div className="ab-test-modal__traffic__header">
              <div>
                <p className="ab-test-modal__traffic__title">Now select the control checkout groups.</p>
                {formData?.controlGroups?.length > 0 &&
                  (() => {
                    const selectedCount = filteredControlGroups.filter((cg) => cg.isSelected).length;
                    const allSelected =
                      selectedCount === filteredControlGroups.length && filteredControlGroups.length > 0;

                    const handleSelectAll = (checked: boolean) => {
                      const testGroupsSum =
                        formData.testGroups
                          ?.filter((g) => g.isSelected)
                          .reduce((sum, g) => sum + g.percentageTraffic, 0) || 0;

                      const remainingPercentage = 100 - testGroupsSum;

                      if (!checked) {
                        setFormData((prevData) => ({
                          ...prevData,
                          controlGroups: prevData.controlGroups.map((controlGroup) => {
                            const isFiltered = filteredControlGroups.some(
                              (fcg) => fcg.widgetExternalId === controlGroup.widgetExternalId,
                            );
                            if (isFiltered) {
                              return { ...controlGroup, isSelected: false, percentageTraffic: 0 };
                            }
                            return controlGroup;
                          }),
                        }));
                      } else {
                        const visibleGroupIds = filteredControlGroups.map((cg) => cg.widgetExternalId);

                        if (visibleGroupIds.length > 0) {
                          const percentagePerControlGroup = Math.floor(remainingPercentage / visibleGroupIds.length);
                          const remainder = remainingPercentage - percentagePerControlGroup * visibleGroupIds.length;

                          setFormData((prevData) => ({
                            ...prevData,
                            controlGroups: prevData.controlGroups.map((controlGroup) => {
                              const isVisible = visibleGroupIds.includes(controlGroup.widgetExternalId);
                              if (isVisible) {
                                const visibleIndex = visibleGroupIds.indexOf(controlGroup.widgetExternalId);
                                const percentage =
                                  visibleIndex === 0
                                    ? percentagePerControlGroup + remainder
                                    : percentagePerControlGroup;
                                return {
                                  ...controlGroup,
                                  isSelected: true,
                                  percentageTraffic: percentage,
                                };
                              }
                              return controlGroup;
                            }),
                          }));
                        }
                      }
                    };

                    return (
                      <Checkbox
                        checked={allSelected}
                        onChange={handleSelectAll}
                        label={allSelected ? `${selectedCount} selected` : 'select all'}
                        disabled={mode === 'view'}
                      />
                    );
                  })()}
              </div>
              <div className="ab-test-modal__traffic__allocation-info">
                {getRemainingControlPercentage}% will be allocated
              </div>
            </div>
            {formData?.controlGroups?.length > 0
              ? (() => {
                  const selectedTestGroupIds =
                    formData?.testGroups
                      ?.filter((tg) => tg.isSelected)
                      .map((tg) => tg.widgetExternalId)
                      .sort()
                      .join(',') || '';

                  const selectedControlGroupIds =
                    formData?.controlGroups
                      .filter((cg) => {
                        const isVisible = filteredControlGroups.some(
                          (fcg) => fcg.widgetExternalId === cg.widgetExternalId,
                        );
                        return isVisible && cg.isSelected;
                      })
                      .map((cg) => cg.widgetExternalId)
                      .sort()
                      .join(',') || '';

                  return (
                    <TrafficAllocationInputV2
                      key={`control-groups-${selectedTestGroupIds}-${selectedControlGroupIds}`}
                      trafficAllocationConfig={{
                        variants: filteredControlGroups,
                      }}
                      onChange={(valid: boolean, variants: TrafficAllocationVariant[]) => {
                        const remainingPercentage = getRemainingControlPercentage;
                        const selectedControlGroups = variants.filter((v) => v.isSelected);

                        const updatedVariantsMap = new Map<number, TrafficAllocationVariant>();

                        if (selectedControlGroups.length > 0) {
                          const percentagePerControlGroup = Math.floor(
                            remainingPercentage / selectedControlGroups.length,
                          );
                          const remainder =
                            remainingPercentage - percentagePerControlGroup * selectedControlGroups.length;

                          variants.forEach((variant) => {
                            if (!variant.isSelected) {
                              updatedVariantsMap.set(variant.widgetExternalId, { ...variant, percentageTraffic: 0 });
                            } else {
                              const selectedIndex = selectedControlGroups.findIndex(
                                (g) => g.widgetExternalId === variant.widgetExternalId,
                              );
                              const percentage =
                                selectedIndex === 0 ? percentagePerControlGroup + remainder : percentagePerControlGroup;

                              updatedVariantsMap.set(variant.widgetExternalId, {
                                ...variant,
                                percentageTraffic: percentage,
                              });
                            }
                          });

                          setControlGroupsValid(
                            selectedControlGroups.length > 0 && remainingPercentage === getRemainingControlPercentage,
                          );
                        } else {
                          variants.forEach((v) => {
                            updatedVariantsMap.set(v.widgetExternalId, { ...v, percentageTraffic: 0 });
                          });
                          setControlGroupsValid(false);
                        }

                        setFormData((prevData) => ({
                          ...prevData,
                          controlGroups: prevData.controlGroups.map((controlGroup) => {
                            const updatedVariant = updatedVariantsMap.get(controlGroup.widgetExternalId);
                            return updatedVariant || controlGroup;
                          }),
                        }));
                      }}
                      viewMode={mode == 'view'}
                      hidePercentage={true}
                      maxTestGroups={MAX_TEST_GROUPS}
                    />
                  );
                })()
              : null}
            <div className="ab-test-modal__actions">
              <Button
                onClick={() => setActiveTab('Select test groups')}
                text={'Back'}
                size={'medium'}
                variant={'secondary'}
                className="ab-test-modal__actions__cancel"
              />
              <Button
                onClick={() => {
                  if (mode === 'view') {
                    handleTabChange('Rules');
                  } else {
                    handleControlGroupsValidation('Rules');
                  }
                }}
                text={'Next'}
                size={'medium'}
                variant={'primary'}
              />
            </div>
          </Tab>
          <Tab label={'Rules'} showTab={true}>
            {initialRulesFromTestGroupsForNewABTest && (
              <div className="ab-test-modal__rules__warning">
                <WarningBanner
                  title="Pay attention"
                  content="Rules from selected checkout groups have been automatically merged. Please review and adjust as needed."
                  hideCloseButton={true}
                  variant="attention"
                />
              </div>
            )}
            <p className="ab-test-modal__rules__title">
              {mode == 'view'
                ? formData?.abTestRules?.ruleGroups?.length > 0
                  ? '*Run test for checkouts that meet the following criteria:'
                  : 'No rules have been set for this test.'
                : '*Run test for checkouts that meet the following criteria:'}
            </p>
            <div className="ab-test-modal__rulesets-container">
              <RuleEngineV2
                key={existingAbTest ? undefined : `rule-engine-${selectedTestGroupIds}`}
                initialState={
                  formData?.abTestRules
                    ? mapRuleEngineApiDtoToRuleEngineState(formData.abTestRules)
                    : initialRulesFromTestGroupsForNewABTest || {
                        matchAllRuleGroups: true,
                        ruleGroups: [],
                      }
                }
                config={AbtestRuleEngineConfig}
                bindings={{
                  viewMode: mode == 'view',
                  showValidationErrors: showInvalidStep == 2,
                  hideMatchAllRuleGroupsTab: shop?.metadata?.enablePredictedSegmentAbTestWinner !== false,
                  onChange: (state: RuleEngineState, valid: boolean) => {
                    setFormData((prevData) => ({
                      ...prevData,
                      abTestRules: mapRuleEngineStateToRuleEngineApiDto(state),
                    }));

                    setRulesValid(valid);
                  },
                  fetchAutocompleteValues: async (property: Property, input: string = ''): Promise<string[]> => {
                    // destination code autocomplete
                    if (property === Property.country) {
                      return Object.values(countryCodeToDisplayNameMap);
                    }
                    const returnObject = await pdqCoreApi.get(
                      `/conditions/autocomplete/fetch?input=${encodeURIComponent(input)}&autocompleteBy=${getKeyByStringValue(Property, property)}`,
                    );
                    return returnObject.data;
                  },
                  showToast: showToast,
                }}
              />
              {existingAbTest?.testType === AbTestTypeCategory.EXTENSIONS && (
                <p className="ab-test-modal__rules__explanation">
                  The display rules configured for each extension will still apply during this test. This ensures that
                  the behavior you see reflects real conditions in the live environment
                </p>
              )}
            </div>
            <div className="ab-test-modal__actions">
              <Button
                onClick={() => setActiveTab('Select control groups')}
                text={'Back'}
                size={'medium'}
                variant={'secondary'}
                className="ab-test-modal__actions__cancel"
              />
              {mode != 'view' && (
                <Button
                  onClick={() => {
                    if (!fullFormValid) {
                      setShowInvalidStep(2);
                    } else {
                      setShowInvalidStep(0);
                      handleSave();
                    }
                  }}
                  text={getButtonText(mode)}
                  size={'medium'}
                  variant={'primary'}
                />
              )}
              {mode == 'view' && (
                <Button
                  onClick={() => {
                    onClose();
                  }}
                  text={'Done'}
                  size={'medium'}
                  variant={'primary'}
                />
              )}
            </div>
          </Tab>
        </Tabs>
      </div>
    </Modal>
  );
};

const getInitialFormData = (existingAbTest?: AbTest): AbTestFormDataV2 => {
  return {
    name: existingAbTest?.name || '',
    description: existingAbTest?.description || '',
    numberOfCheckouts: existingAbTest?.numberOfCheckouts,
    optimizingFor: existingAbTest
      ? existingAbTest.optimizingFor
      : getKeyByStringValue(AbTestOptimizationTarget, AbTestOptimizationTarget.CHECKOUT_CONVERSION),
    statisticalSignificance: existingAbTest ? existingAbTest?.statisticalSignificance : 95,
    testGroups: [],
    controlGroups: [],
    abTestRules: existingAbTest?.abTestRules,
    testType: existingAbTest?.testType || AbTestTypeCategory.SHIPPING_RATES,
  };
};
function getButtonText(mode: string): string {
  const modeMap = {
    create: 'Create draft test',
    edit: 'Save draft test',
    duplicate: 'Create draft test',
  };

  return modeMap[mode] || 'Save draft test';
}
